<template>
  <AppLayout>
    <div class="flex flex-col min-h-full bg-slate-50 dark:bg-slate-950">

      <!-- Sticky top bar -->
      <div class="sticky top-0 z-20 bg-slate-50 dark:bg-slate-950 px-4 pt-4 pb-0">
        <div class="flex items-center justify-between mb-3">
          <div>
            <h1 class="text-xl font-bold text-slate-800 dark:text-white leading-tight">Cash Flow</h1>
            <p class="text-xs text-slate-400 mt-0.5">{{ periodLabel }}</p>
          </div>
          <button @click="showFilters = !showFilters"
            class="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition"
            :class="showFilters
              ? 'bg-indigo-500 text-white'
              : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"/>
            </svg>
            Filters
          </button>
        </div>

        <div v-if="showFilters"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 mb-3">
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Period</label>
              <select v-model="filters.preset"
                class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Group By</label>
              <select v-model="filters.groupBy"
                class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <template v-if="filters.preset === 'custom'">
              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">From</label>
                <input type="date" v-model="filters.from"
                  class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">To</label>
                <input type="date" v-model="filters.to"
                  class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="flex-1 px-4 pb-6 pt-3 space-y-4">

        <!-- KPI grid -->
        <div class="grid grid-cols-3 lg:grid-cols-6 gap-3">
          <div v-for="kpi in cashKPIs" :key="kpi.label"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3 lg:p-4">
            <p class="text-[9px] lg:text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{{ kpi.label }}</p>
            <p class="text-sm lg:text-base font-bold leading-tight" :class="kpi.color">{{ kpi.value }}</p>
            <p v-if="kpi.sub" class="text-[9px] lg:text-[10px] mt-1" :class="kpi.subColor || 'text-slate-400'">{{ kpi.sub }}</p>
          </div>
        </div>

        <!-- Row: Received vs Sent line + Net cash bar -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Received vs Sent trend -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Cash In vs Cash Out</p>
            <Line :data="cashFlowChartData" :options="lineOptions" style="max-height:200px" />
          </div>

          <!-- Net cash flow bar (positive/negative) -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Net Cash Flow by Month</p>
            <Bar :data="netCashBarData" :options="netBarOptions" style="max-height:200px" />
          </div>

        </div>

        <!-- Row: Best/Worst/Avg highlights -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div class="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900 rounded-2xl p-4">
            <p class="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-1">Best Month</p>
            <p class="text-lg font-bold text-emerald-700 dark:text-emerald-300">October</p>
            <p class="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">Rs 27k net · 72k received</p>
          </div>
          <div class="bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900 rounded-2xl p-4">
            <p class="text-[10px] font-semibold text-red-500 dark:text-red-400 uppercase tracking-wide mb-1">Tightest Month</p>
            <p class="text-lg font-bold text-red-600 dark:text-red-300">January</p>
            <p class="text-xs text-red-500 dark:text-red-400 mt-0.5">Rs 10k net · 28k received</p>
          </div>
          <div class="bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900 rounded-2xl p-4">
            <p class="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide mb-1">Avg Monthly</p>
            <p class="text-lg font-bold text-indigo-700 dark:text-indigo-300">Rs 11.25k</p>
            <p class="text-xs text-indigo-500 dark:text-indigo-400 mt-0.5">net cash per month</p>
          </div>
        </div>

        <!-- Row: Cash In sources + Cash Out sources -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Cash In by source -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Cash In by Source</p>
              <span class="text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full">Rs 3.20L total</span>
            </div>
            <div class="space-y-3">
              <div v-for="s in cashInSources" :key="s.source">
                <div class="flex items-center justify-between mb-1.5">
                  <div class="flex items-center gap-2">
                    <div class="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" :style="{ backgroundColor: s.bg }">
                      <span class="text-xs">{{ s.icon }}</span>
                    </div>
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300">{{ s.source }}</span>
                  </div>
                  <div class="text-right">
                    <span class="text-xs font-bold text-slate-800 dark:text-white">{{ shortNum(s.amount) }}</span>
                    <span class="text-[10px] text-slate-400 ml-1">{{ s.count }} txn</span>
                  </div>
                </div>
                <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div class="h-full rounded-full" :style="{ width: s.pct + '%', backgroundColor: s.color }"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Cash Out by source -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Cash Out by Source</p>
              <span class="text-[10px] font-semibold bg-red-50 dark:bg-red-950 text-red-500 dark:text-red-400 px-2 py-0.5 rounded-full">Rs 1.85L total</span>
            </div>
            <div class="space-y-3">
              <div v-for="s in cashOutSources" :key="s.source">
                <div class="flex items-center justify-between mb-1.5">
                  <div class="flex items-center gap-2">
                    <div class="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" :style="{ backgroundColor: s.bg }">
                      <span class="text-xs">{{ s.icon }}</span>
                    </div>
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300">{{ s.source }}</span>
                  </div>
                  <div class="text-right">
                    <span class="text-xs font-bold text-slate-800 dark:text-white">{{ shortNum(s.amount) }}</span>
                    <span class="text-[10px] text-slate-400 ml-1">{{ s.count }} txn</span>
                  </div>
                </div>
                <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div class="h-full rounded-full" :style="{ width: s.pct + '%', backgroundColor: s.color }"></div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Row: Top customers cash in + Top customers cash out -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Top customers by cash received -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Top 5 — Cash Received</p>
              <span class="text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full">from customers</span>
            </div>
            <div class="space-y-3">
              <div v-for="c in topCashIn" :key="c.name" class="flex items-center gap-3">
                <div class="w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center text-[10px] font-bold text-emerald-600 dark:text-emerald-400 shrink-0">
                  {{ c.name.charAt(0) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between mb-1">
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                    <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400 ml-2 shrink-0">{{ shortNum(c.amount) }}</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-emerald-500 rounded-full" :style="{ width: c.pct + '%' }"></div>
                  </div>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ c.txn }} payments</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Top customers by cash sent -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Top 5 — Cash Sent</p>
              <span class="text-[10px] font-semibold bg-red-50 dark:bg-red-950 text-red-500 dark:text-red-400 px-2 py-0.5 rounded-full">to customers</span>
            </div>
            <div class="space-y-3">
              <div v-for="c in topCashOut" :key="c.name" class="flex items-center gap-3">
                <div class="w-7 h-7 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center text-[10px] font-bold text-red-500 dark:text-red-400 shrink-0">
                  {{ c.name.charAt(0) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between mb-1">
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                    <span class="text-xs font-bold text-red-500 dark:text-red-400 ml-2 shrink-0">{{ shortNum(c.amount) }}</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-red-500 rounded-full" :style="{ width: c.pct + '%' }"></div>
                  </div>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ c.txn }} payments</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Monthly cash flow breakdown -->
        <div>
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Monthly Breakdown</p>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div v-for="row in monthlyCashFlow" :key="row.month"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
              <div class="flex items-center justify-between mb-3">
                <div>
                  <p class="font-semibold text-slate-800 dark:text-white text-sm">{{ row.month }}</p>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ row.txn }} transactions</p>
                </div>
                <div class="text-right">
                  <span class="text-sm font-bold px-2.5 py-1 rounded-xl block"
                    :class="row.net >= 0 ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-950 text-red-500 dark:text-red-400'">
                    {{ row.net >= 0 ? '+' : '' }}{{ shortNum(row.net) }}
                  </span>
                  <p class="text-[10px] mt-1 font-semibold"
                    :class="row.mom > 0 ? 'text-emerald-500' : row.mom < 0 ? 'text-red-400' : 'text-slate-400'">
                    {{ row.mom > 0 ? '↑' : row.mom < 0 ? '↓' : '—' }} {{ Math.abs(row.mom) }}% MoM
                  </p>
                </div>
              </div>
              <div class="grid grid-cols-3 gap-2 text-center">
                <div class="bg-emerald-50 dark:bg-emerald-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Received</p>
                  <p class="text-xs font-bold text-emerald-600 dark:text-emerald-400">{{ shortNum(row.received) }}</p>
                </div>
                <div class="bg-red-50 dark:bg-red-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Sent</p>
                  <p class="text-xs font-bold text-red-500 dark:text-red-400">{{ shortNum(row.sent) }}</p>
                </div>
                <div class="bg-indigo-50 dark:bg-indigo-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Ratio</p>
                  <p class="text-xs font-bold text-indigo-600 dark:text-indigo-400">{{ row.ratio }}%</p>
                </div>
              </div>
              <!-- Net bar -->
              <div class="mt-3">
                <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div class="h-full rounded-full transition-all"
                    :class="row.net >= 0 ? 'bg-emerald-500' : 'bg-red-500'"
                    :style="{ width: Math.min(Math.abs(row.ratio), 100) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Line, Bar } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler } from 'chart.js'
import AppLayout from '@/layouts/AppLayout.vue'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler)

const page = usePage()
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')
onMounted(() => { document.title = `Cash Flow | ${businessName.value}` })

const showFilters = ref(false)
const filters = ref({ preset: 'this_year', groupBy: 'monthly', from: '', to: '' })
const periodLabel = computed(() => {
  const map = { this_month: 'This Month', last_month: 'Last Month', this_year: 'This Year', last_year: 'Last Year', custom: 'Custom Range' }
  return map[filters.value.preset] || 'Custom Range'
})

function shortNum(n) {
  n = Number(n) || 0
  if (n >= 100000) return 'Rs ' + (n / 100000).toFixed(1) + 'L'
  if (n >= 1000)   return 'Rs ' + (n / 1000).toFixed(1) + 'k'
  return 'Rs ' + n
}

const MONTHS    = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const tickColor = '#94a3b8'
const gridColor = 'rgba(148,163,184,0.12)'
const baseFont  = { family: 'inherit', size: 10 }

// ── KPIs ─────────────────────────────────────────────
const cashKPIs = [
  { label: 'Cash Received', value: 'Rs 3.20L', color: 'text-emerald-600 dark:text-emerald-400', sub: '41 payments' },
  { label: 'Cash Sent',     value: 'Rs 1.85L', color: 'text-red-500 dark:text-red-400',         sub: '26 payments' },
  { label: 'Net Cash Flow', value: 'Rs 1.35L', color: 'text-slate-800 dark:text-white',          sub: 'Positive', subColor: 'text-emerald-500' },
  { label: 'Transactions',  value: '67',        color: 'text-indigo-600 dark:text-indigo-400',   sub: 'total payments' },
  { label: 'Avg Monthly In',value: 'Rs 26.7k', color: 'text-emerald-600 dark:text-emerald-400', sub: 'received/month' },
  { label: 'Avg Monthly Out',value:'Rs 15.4k', color: 'text-red-500 dark:text-red-400',         sub: 'sent/month' },
]

// ── Line chart ────────────────────────────────────────
const lineOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { labels: { color: tickColor, font: baseFont, boxWidth: 10, padding: 12 } }, tooltip: { mode: 'index' } },
  scales: {
    x: { grid: { display: false }, ticks: { color: tickColor, font: baseFont } },
    y: { grid: { color: gridColor }, ticks: { color: tickColor, font: baseFont, callback: v => (v / 1000) + 'k' } },
  },
  elements: { line: { tension: 0.4 }, point: { radius: 2, hoverRadius: 5 } },
}

const receivedData = [28000,32000,45000,38000,52000,61000,55000,49000,68000,72000,64000,56000]
const sentData     = [18000,22000,28000,25000,34000,40000,36000,31000,44000,45000,42000,34000]

const cashFlowChartData = {
  labels: MONTHS,
  datasets: [
    { label: 'Received', data: receivedData, borderColor: 'rgba(16,185,129,1)',  backgroundColor: 'rgba(16,185,129,0.06)', fill: false },
    { label: 'Sent',     data: sentData,     borderColor: 'rgba(239,68,68,1)',   backgroundColor: 'rgba(239,68,68,0.06)',  fill: false },
  ],
}

// ── Net cash bar (positive = green, negative = red) ──
const netBarOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { mode: 'index' } },
  scales: {
    x: { grid: { display: false }, ticks: { color: tickColor, font: baseFont } },
    y: { grid: { color: gridColor }, ticks: { color: tickColor, font: baseFont, callback: v => (v / 1000) + 'k' } },
  },
}
const netData = receivedData.map((r, i) => r - sentData[i])
const netCashBarData = {
  labels: MONTHS,
  datasets: [{
    label: 'Net Cash',
    data: netData,
    backgroundColor: netData.map(v => v >= 0 ? 'rgba(16,185,129,0.85)' : 'rgba(239,68,68,0.85)'),
    borderRadius: 4,
  }],
}

// ── Cash In sources ───────────────────────────────────
const cashInSources = [
  { source: 'Cash',          amount: 145000, count: 32, pct: 100, color: 'rgba(99,102,241,0.9)',  bg: 'rgba(99,102,241,0.1)',  icon: '💵' },
  { source: 'Bank Transfer', amount: 98000,  count: 18, pct: 68,  color: 'rgba(16,185,129,0.9)',  bg: 'rgba(16,185,129,0.1)',  icon: '🏦' },
  { source: 'Cheque',        amount: 52000,  count: 12, pct: 36,  color: 'rgba(245,158,11,0.9)',  bg: 'rgba(245,158,11,0.1)',  icon: '📄' },
  { source: 'Online',        amount: 25000,  count: 5,  pct: 17,  color: 'rgba(59,130,246,0.9)',  bg: 'rgba(59,130,246,0.1)',  icon: '📱' },
]

// ── Cash Out sources ──────────────────────────────────
const cashOutSources = [
  { source: 'Cash',          amount: 82000,  count: 14, pct: 100, color: 'rgba(239,68,68,0.85)',  bg: 'rgba(239,68,68,0.1)',   icon: '💵' },
  { source: 'Bank Transfer', amount: 61000,  count: 8,  pct: 74,  color: 'rgba(245,158,11,0.85)', bg: 'rgba(245,158,11,0.1)',  icon: '🏦' },
  { source: 'Cheque',        amount: 42000,  count: 4,  pct: 51,  color: 'rgba(99,102,241,0.85)', bg: 'rgba(99,102,241,0.1)',  icon: '📄' },
]

// ── Top customers cash in ─────────────────────────────
const topCashIn = [
  { name: 'Tariq Javaid',    amount: 72000, txn: 5, pct: 100 },
  { name: 'Pervez Iqbal',    amount: 58000, txn: 4, pct: 81  },
  { name: 'Hamid Mirza',     amount: 41000, txn: 6, pct: 57  },
  { name: 'Naveed Siddiqui', amount: 35000, txn: 3, pct: 49  },
  { name: 'Rashid Ali',      amount: 22000, txn: 4, pct: 31  },
]

// ── Top customers cash out ────────────────────────────
const topCashOut = [
  { name: 'Waqar Younis',   amount: 48000, txn: 4, pct: 100 },
  { name: 'Imran Khan',     amount: 36000, txn: 3, pct: 75  },
  { name: 'Zafar Iqbal',    amount: 28000, txn: 5, pct: 58  },
  { name: 'Sajid Mehmood',  amount: 19000, txn: 2, pct: 40  },
  { name: 'Khalid Mehmood', amount: 14000, txn: 3, pct: 29  },
]

// ── Monthly breakdown ─────────────────────────────────
const monthlyCashFlow = MONTHS.slice().reverse().map((month, i) => {
  const idx = 11 - i
  const received = receivedData[idx]
  const sent     = sentData[idx]
  const net      = received - sent
  const ratio    = Math.round((net / received) * 100)
  const prevNet  = idx > 0 ? receivedData[idx - 1] - sentData[idx - 1] : net
  const mom      = prevNet !== 0 ? Math.round(((net - prevNet) / Math.abs(prevNet)) * 100) : 0
  return { month: `${month} 2025`, received, sent, net, ratio, mom, txn: Math.floor(Math.random() * 8) + 3 }
})
</script>

<style scoped>
.scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
.scrollbar-hide::-webkit-scrollbar { display: none; }
</style>
